import { iTrack } from '../types/ITrack';
import { AssetProvider } from '../types/MatchTypes';
import { StreamingServiceId } from '../types/Streaming';
import { findBestCloudMatch } from '../utils/cloudCrateMatch';
import AppleMusicService from './AppleMusic.service';
import SpotifyService from './Spotify.service';
import TidalService from './Tidal.service';

/**
 * Matching a crate track to its counterpart on a streaming service. Shared by
 * the crate save buttons and the agent's streaming export so both land on the
 * same track for the same crate.
 */

export interface StreamingMatchResult {
  matched: iTrack | null;
  /** Why nothing matched, shown next to the track after a save. */
  reason?: string;
}

const PROVIDERS: Record<StreamingServiceId, AssetProvider> = {
  spotify: 'spotify',
  apple: 'apple_music',
  tidal: 'tidal',
};

const searchService = async (
  serviceId: StreamingServiceId,
  query: string,
): Promise<iTrack[]> => {
  if (serviceId === 'spotify') {
    const results = await SpotifyService.SearchTracks(query);
    return results?.tracks || [];
  }
  if (serviceId === 'apple') return AppleMusicService.searchTracks(query);
  return TidalService.searchTracks(query);
};

const searchByIsrc = async (
  serviceId: StreamingServiceId,
  isrc: string,
): Promise<iTrack[]> => {
  if (serviceId === 'tidal') return TidalService.searchByIsrc(isrc);
  if (serviceId === 'apple') return AppleMusicService.searchByIsrc(isrc);
  const results = await SpotifyService.SearchTracks(`isrc:${isrc}`);
  return results?.tracks || [];
};

/** Queries to try in order. The full title comes first so remix and edit info survives. */
const searchQueriesFor = (track: iTrack): string[] => {
  const trackArtist = track.Artist || '';
  const trackTitle = track.Title || '';
  const primaryArtist = trackArtist.split(/,/)[0]?.trim() || '';
  const cleanTitle = trackTitle
    .replace(/\s*\(.*?\)/g, '')
    .replace(/\s*-\s*.*$/, '')
    .replace(/\.{2,}/g, '')
    .trim();

  return [
    `${primaryArtist} ${trackTitle}`,
    `${primaryArtist} ${cleanTitle}`,
    `${trackArtist} ${trackTitle}`,
  ].filter((query, index, all) => all.indexOf(query) === index);
};

/** ISRC first, then progressively looser searches. */
export const findStreamingMatch = async (
  track: iTrack,
  serviceId: StreamingServiceId,
): Promise<StreamingMatchResult> => {
  const provider = PROVIDERS[serviceId];
  let reason: string | undefined;

  try {
    if (track.isrc) {
      const candidates = await searchByIsrc(serviceId, track.isrc);
      const match = findBestCloudMatch(track, candidates, provider);
      if (match.found && match.matchedTrack) {
        return { matched: match.matchedTrack };
      }
    }

    for (const query of searchQueriesFor(track)) {
      const candidates = await searchService(serviceId, query);
      if (!candidates.length) continue;

      const match = findBestCloudMatch(track, candidates, provider);
      if (match.found && match.matchedTrack) {
        return { matched: match.matchedTrack };
      }
      reason = match.reason;
    }

    return { matched: null, reason: reason || 'Not found' };
  } catch (error) {
    console.error(`[StreamingMatch] Search error for ${serviceId}:`, error);
    return { matched: null, reason: 'Search failed' };
  }
};
